import { t } from "i18next";
import { useEffect, useState } from "react";
import { Alert, Modal, Table } from "react-bootstrap";
import moment from 'moment';
import { LinesChart } from "../../../charts/lines-chart";
import { MonitoringShovelfulsChart } from "../../../charts/monitoring-shovelfuls-chart";
import { MonitoringLaptimesChart } from "../../../charts/monitoring-laptimes-chart";
import { PlanningReportingDaySession, PlanningReportingMonitoring, PlanningReportingAthlete, PlanningMonitoringAthlete } from "../../../../interfaces/planning";

interface CoachPlanningMomentSessionDetailsProps {
    session: PlanningReportingDaySession;
    close: CallableFunction;
}

export function CoachPlanningMomentSessionDetails(props: CoachPlanningMomentSessionDetailsProps) {
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [duration, setDuration] = useState('');
    const [totalLaps, setTotalLaps] = useState(0);
    const [averageShovelfulsPerMinute, setAverageShovelfulsPerMinute] = useState(0);
    const [lapTimes, setLapTimes] = useState<number[]>([]);
    const [shovelfulsPerMinute, setShovelfulsPerMinute] = useState<number[]>([]);
    const [shovelfulsTimestamps, setShovelfulsTimestamps] = useState<string[]>([]);

    useEffect(() => {
        // Procesar todos los datos desde el array de monitorización
        processMonitoringData();
    }, []);

    const processMonitoringData = () => {
        const startEvent = props.session.monitoring.find((m: PlanningReportingMonitoring) => m.event_type === 'Training start');
        const finishEvent = props.session.monitoring.find((m: PlanningReportingMonitoring) => m.event_type === 'Training finish');
        const lapEvents = props.session.monitoring.filter((m: PlanningReportingMonitoring) => m.event_type === 'lap');
        const shovelfulEvents = props.session.monitoring.filter((m: PlanningReportingMonitoring) => m.event_type === 'Shovelful');

        // Establecer hora de inicio desde el array de monitorización
        if (startEvent) {
            setStart(startEvent.event_time);
        }

        // Establecer hora de fin desde el array de monitorización
        if (finishEvent) {
            setEnd(finishEvent.event_time);
        } else {
            // Si no hay evento de finalización, usar el último evento
            const lastElement = props.session.monitoring.at(-1);
            if (lastElement) {
                setEnd(lastElement.event_time);
            }
        }

        // Calcular duración total desde el array de monitorización
        if (startEvent && finishEvent) {
            const startMoment = moment(startEvent.event_time);
            const finishMoment = moment(finishEvent.event_time);
            const durationMoment = moment.duration(finishMoment.diff(startMoment));
            const hours = Math.floor(durationMoment.asHours());
            const minutes = durationMoment.minutes();
            const seconds = durationMoment.seconds();
            setDuration(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        }

        // Total de parciales desde el array de monitorización
        setTotalLaps(lapEvents.length);

        // Calcular promedio de paladas por minuto (event_value ya contiene paladas/min)
        let avgShovelfulsPerMin = 0;

        if (shovelfulEvents.length > 0) {
            let totalStrokesPerMinute = 0;
            shovelfulEvents.forEach((event: PlanningReportingMonitoring) => {
                const strokesPerMin = parseFloat(event.event_value || '0');
                if (strokesPerMin > 0) {
                    totalStrokesPerMinute += strokesPerMin;
                }
            });
            avgShovelfulsPerMin = totalStrokesPerMinute / shovelfulEvents.length;
        }
        setAverageShovelfulsPerMinute(avgShovelfulsPerMin);

        // Calcular tiempos de parciales
        const lapTimesArray: number[] = [];
        if (startEvent && lapEvents.length > 0) {
            let previousTime = moment(startEvent.event_time);

            lapEvents.forEach((lap: PlanningReportingMonitoring) => {
                const lapTime = moment(lap.event_time);
                const lapDuration = moment.duration(lapTime.diff(previousTime));
                const lapSeconds = lapDuration.asSeconds();
                lapTimesArray.push(lapSeconds);
                previousTime = lapTime;
            });
        }

        // Preparar datos para el gráfico (event_value ya contiene paladas/min)
        const shovelfulsPerMinuteArray: number[] = [];
        const shovelfulsTimestampsArray: string[] = [];

        shovelfulEvents.forEach((event: PlanningReportingMonitoring) => {
            const strokesPerMin = parseFloat(event.event_value || '0');
            if (strokesPerMin > 0) {
                shovelfulsPerMinuteArray.push(strokesPerMin);

                // Guardar el timestamp del evento
                const eventTime = moment(event.event_time);
                shovelfulsTimestampsArray.push(eventTime.format('HH:mm:ss'));
            }
        });

        setLapTimes(lapTimesArray);
        setShovelfulsPerMinute(shovelfulsPerMinuteArray);
        setShovelfulsTimestamps(shovelfulsTimestampsArray);
    };

    const handleClose = () => {
        props.close();
    }

    return (
        <Modal className="moment-details-container" show={ true }
            onHide={ handleClose } size="xl"> 
            <Modal.Header closeButton>
                <Modal.Title>
                    { t("Details") }
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="summary-boxes">
                    <div className="summary-box">
                        <div className="label">{ t("Start time") }</div>
                        <div className="value">{ start ? moment(start).format('HH:mm:ss') : '-' }</div>
                    </div>
                    <div className="summary-box">
                        <div className="label">{ t("Finish time") }</div>
                        <div className="value">{ end ? moment(end).format('HH:mm:ss') : '-' }</div>
                    </div>
                    <div className="summary-box">
                        <div className="label">{ t("Total duration") }</div>
                        <div className="value">{ duration || '-' }</div>
                    </div>
                    <div className="summary-box">
                        <div className="label">{ t("Total laps") }</div>
                        <div className="value">{ totalLaps > 0 ? totalLaps : '-' }</div>
                    </div>
                    <div className="summary-box">
                        <div className="label">{ t("Average strokes/min") }</div>
                        <div className="value">{ averageShovelfulsPerMinute > 0 ? averageShovelfulsPerMinute.toFixed(1) : '-' }</div>
                    </div>
                    <div className="summary-box">
                        <div className="label">{ t("Temperature") }</div>
                        <div className="value">{ props.session.avg_temperature ? `${props.session.avg_temperature}ºC` : '-' }</div>
                    </div>
                    <div className="summary-box">
                        <div className="label">{ t("Wind speed") }</div>
                        <div className="value">{ props.session.avg_wind_speed ? `${props.session.avg_wind_speed} k/h` : '-' }</div>
                    </div>
                </div>
                { props.session.monitoring.length == 0 &&
                    <Alert key={'info'} variant={'info'}>
                        { t("This activty has no details.") }
                    </Alert>
                }
                { shovelfulsPerMinute.length > 0 &&
                    <div className="moment-details">
                        <MonitoringShovelfulsChart data={shovelfulsPerMinute} timestamps={shovelfulsTimestamps} />
                    </div>
                }
                { lapTimes.length > 0 &&
                    <div className="boyas">
                        <MonitoringLaptimesChart data={lapTimes} />
                    </div>
                }
                { props.session.monitoring_meta && props.session.monitoring_meta.observations &&
                    <div className="coach-comments">
                        <h4>{ t("Coach comments") }</h4>
                        <div className="comments-content">
                            { props.session.monitoring_meta.observations }
                        </div>
                    </div>
                }
                { props.session.monitoring_athletes && props.session.monitoring_athletes.length > 0 &&
                    <div className="participating-athletes">
                        <h4>{ t("Participating athletes") }</h4>
                        <div className="athletes-badges">
                            { props.session.monitoring_athletes.map((athlete: PlanningMonitoringAthlete) => {
                                return (
                                    <a
                                        key={athlete.athlete_id}
                                        href={`#/athletes/${athlete.athlete_id}/view`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="athlete-badge"
                                    >
                                        { athlete.athlete_name }
                                    </a>
                                );
                            })}
                        </div>
                    </div>
                }
                { props.session.athletes && props.session.athletes.length > 0 &&
                    <div className="athletes">
                        <h4>{ t("Athletes feedback") }</h4>
                        <div className="athletes-list">
                            <Table>
                                <thead>
                                    <tr>
                                        <th>{ t("Name") }</th>
                                        <th>{ t("Fatigue level") }</th>
                                        <th>{ t("Observations") }</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    { props.session.athletes.map((a: PlanningReportingAthlete) => {
                                        return <tr key={a.athlete_id}>
                                                <td>{ a.athlete_name }</td>
                                                <td>{ a.fatigue_level }</td>
                                                <td>{ a.observations }</td>
                                            </tr>;
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    </div>
                }
            </Modal.Body>
        </Modal>
    );
}